import type { TypeAction, PaletteColor, ColorSystemOptions, PaletteColorChannel } from '@mui/material/styles';
import type { SchemesRecord } from '../types';

import { varAlpha, createPaletteChannel } from 'minimal-shared/utils';

import { opacity } from './opacity';
import { themeConfig } from '../theme-config';

// ----------------------------------------------------------------------

/**
 * TypeScript extension for MUI theme augmentation.
 * @to {@link file://./../extend-theme-types.d.ts}
 */

// Keys for core palette colors
export type PaletteColorKey = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
export type CommonColorsKeys = 'black' | 'white';

// Palette color without channels
export type PaletteColorNoChannels = Omit<PaletteColor, 'lighterChannel' | 'darkerChannel'>;

// Palette color with channels
export type PaletteColorWithChannels = PaletteColor & PaletteColorChannel;

// Extended palette color shades
export type PaletteColorExtend = {
  lighter: string;
  darker: string;
  lighterChannel: string;
  darkerChannel: string;
};

// Extended common colors
export type CommonColorsExtend = {
  whiteChannel: string;
  blackChannel: string;
};

// Extended text colors
export type TypeTextExtend = {
  disabledChannel: string;
};

// Extended background colors
export type TypeBackgroundExtend = {
  neutral: string;
  neutralChannel: string;
};

// Extended grey colors (Tailwind neutral 50-950)
export type GreyExtend = {
  950: string;
  '50Channel': string;
  '100Channel': string;
  '200Channel': string;
  '300Channel': string;
  '400Channel': string;
  '500Channel': string;
  '600Channel': string;
  '700Channel': string;
  '800Channel': string;
  '900Channel': string;
  '950Channel': string;
};

// Container semantic colors (Ari design system)
export type ContainerColors = {
  low: string;
  lowChannel: string;
  medium: string;
  mediumChannel: string;
  high: string;
  highChannel: string;
};

// Extended palette
export type PaletteExtend = {
  shared: {
    inputOutlined: string;
    inputUnderline: string;
    paperOutlined: string;
    buttonOutlined: string;
  };
  container: ContainerColors;
};

/**
 * ➤
 * ➤ ➤ Core palette (primary, secondary, info, success, warning, error, common, grey)
 * ➤
 */
export const primary = createPaletteChannel(themeConfig.palette.primary);
export const secondary = createPaletteChannel(themeConfig.palette.secondary);
export const info = createPaletteChannel(themeConfig.palette.info);
export const success = createPaletteChannel(themeConfig.palette.success);
export const warning = createPaletteChannel(themeConfig.palette.warning);
export const error = createPaletteChannel(themeConfig.palette.error);
export const common = createPaletteChannel(themeConfig.palette.common);
export const grey = createPaletteChannel(themeConfig.palette.grey);

/**
 * ➤
 * ➤ ➤ Text, background, action
 * ➤
 */
export const text = {
  light: createPaletteChannel({
    primary: grey[900],
    secondary: grey[700],
    disabled: grey[600],
  }),
  dark: createPaletteChannel({
    primary: '#FFFFFF',
    secondary: grey[400],
    disabled: grey[700],
  }),
};

export const background = {
  light: createPaletteChannel({
    paper: '#FFFFFF',
    default: '#FFFFFF',
    neutral: grey[300],
  }),
  dark: createPaletteChannel({
    paper: '#141414',
    default: '#000000',
    neutral: '#18181B',
  }),
};

export const action = (mode: 'light' | 'dark'): Partial<TypeAction> => ({
  active: mode === 'light' ? grey[700] : grey[600],
  hover: varAlpha(grey['600Channel'], 0.08),
  selected: varAlpha(grey['600Channel'], 0.16),
  focus: varAlpha(grey['600Channel'], 0.24),
  disabled: varAlpha(grey['600Channel'], 0.8),
  disabledBackground: varAlpha(grey['600Channel'], 0.24),
  hoverOpacity: 0.08,
  selectedOpacity: 0.08,
  focusOpacity: 0.12,
  activatedOpacity: 0.12,
  disabledOpacity: 0.48,
});

/**
 * ➤
 * ➤ ➤ Extended palette
 * ➤
 */
export const extendPalette: PaletteExtend = {
  shared: {
    inputUnderline: varAlpha(grey['600Channel'], opacity.inputUnderline),
    inputOutlined: varAlpha(grey['600Channel'], 0.2),
    paperOutlined: varAlpha(grey['600Channel'], 0.16),
    buttonOutlined: varAlpha(grey['600Channel'], 0.32),
  },
  container: {
    low: grey[900], // #171717
    lowChannel: grey['900Channel'],
    medium: grey[800], // #262626
    mediumChannel: grey['800Channel'],
    high: grey[700], // #404040
    highChannel: grey['700Channel'],
  },
};

/**
 * ➤
 * ➤ ➤ Base configuration
 * ➤
 */
const basePalette: ColorSystemOptions['palette'] = {
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  common,
  grey,
  TableCell: { border: varAlpha(grey['600Channel'], 0.2) },
  ...extendPalette,
};

/* **********************************************************************
 * 📦 Final
 * **********************************************************************/
export const palette: SchemesRecord<ColorSystemOptions['palette']> = {
  light: {
    ...basePalette,
    text: text.light,
    background: background.light,
    action: action('light'),
    // Per-scheme hairline: a subtle pure-black tint on light surfaces. The old
    // shared grey[700] (#404040) read as a hard near-black line on white.
    divider: varAlpha(common.blackChannel, 0.08),
  },
  dark: {
    ...basePalette,
    text: text.dark,
    background: background.dark,
    action: action('dark'),
    // Subtle white tint on dark — matches the auth surface's own divider and
    // is lighter than the old shared #404040 (~25% white) which read heavy.
    divider: varAlpha(common.whiteChannel, 0.12),
  },
};

export const colorKeys: {
  palette: PaletteColorKey[];
  common: CommonColorsKeys[];
} = {
  palette: ['primary', 'secondary', 'info', 'success', 'warning', 'error'],
  common: ['black', 'white'],
};
